/**
 * Swyft shared types across FE and BE
 */
import { Currency, Dinero } from "dinero.js";
import * as admin from "firebase-admin";
import { Polygon } from "geojson";
import FieldValue = admin.firestore.FieldValue;
import Timestamp = admin.firestore.Timestamp;

/* =================== Auth Related =================== */

export interface IUserBase {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  permissions?: UserPermissions[];
  type?: UserType;
  company?: string;
  roles?: string[];
  status?: UserStatus;
  role?: UserRoleOptions;
  organizationId?: string;
  isOnboardingComplete?: boolean;

  // Bookkeeping
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
  deletedAt?: string | FieldValue;
}

export interface IUserDSP extends IUserBase {
  dspId: string;
}

export interface IUserMerchant extends IUserBase {
  merchantId: string;
}

export interface IUserMerchantGroup extends IUserBase {
  merchantGroupId: string;
}

export type IUser = IUserDSP | IUserMerchant | IUserMerchantGroup;

export interface ICreateUserCallableRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  permissions: UserPermissions[];
  type: UserType;
  company?: string;
  merchantGroupId?: string;
  merchantId?: string;
}

export interface IDeleteUserRequest {
  id: string;
}

export enum UserStatus {
  /** User can use portal, default status for users */
  ACTIVE = "ACTIVE",
  /** User cannot use portal, org admins can change user status to inactive */
  INACTIVE = "INACTIVE",
}

export enum UserRoleOrganizations {
  MERCHANT = "merchant",
  DSP = "dsp",
}

export enum UserRoleOptions {
  ADMIN = "admin",
  MEMBER = "member",
}

export enum UserPermissions {
  ADMIN = "ADMIN",
  SUPERADMIN = "SUPERADMIN",
}

export enum UserType {
  MERCHANT = "MERCHANT",
  MERCHANT_GROUP = "MERCHANT_GROUP",
  WAREHOUSE = "WAREHOUSE",
  GATSBY = "GATSBY",
  INTERNAL = "INTERNAL",
  DRIVER = "DRIVER",
  DRIVER_UNMANAGED = "DRIVER_UNMANAGED",
  WAREHOUSE_UNMANAGED = "WAREHOUSE_UNMANAGED",
}

/* =================== Organization Related =================== */
export interface IOrganization {
  // ID of the org in firestore
  id?: string; // $todo: id is required

  // Unique ID of the given organization, human readable
  slug: string;

  // Name of the organization displayed on labels. Must be unique
  businessName: string;

  // The type of organization
  type: OrganizationType;

  // The 3PL that handles this merchant (if type === merchant)
  _3pl?: number;

  // Contact information for the organization
  contact: IRecipient;

  // List of allowed shipping rates for a organization
  rates: Array<IRate>;

  // Feature Flags
  flags?: Array<string>;

  // Pick up instructions for organization
  pickupNotes?: string;
}

export enum OrganizationType {
  DSP = "DSP",
  _3PL = "3PL",
  CROSSDOCK = "crossdock",
  MERCHANT = "merchant",
  SWYFT = "swyft",
}

/* =================== Label Related =================== */

export interface ILabel {
  // xid generated by application
  id?: string;

  // ID of the Organization that owns the label
  organizationId: string;

  organizationSlug: string;

  orderNumber?: string;

  // Name of the business shipping the package.
  // 3PL's operate as other businesses so may provide
  // multiple different strings here
  businessName: string;

  // Merchant / 3PL pickup location
  origin: IRecipient;

  // Customer drop-off location
  destination: IRecipient;

  // Rate used at time of label creation
  rate: IRate;

  // Notes for driver when delivering
  notes?: string;

  // Reference # or ID on the Organization side in case of any issues.
  // Primarily to be used when our dispatcher reaches out to an Organization,
  // but can also be used by an Organization to attach specific metadata to label.
  reference?: string;
  reference2?: string;

  // Estimates for delivery window
  completeAfter: string;
  completeBefore: string;

  // Time when the order was completed i.e. either delivered or failed
  completionTime?: Timestamp;
  photoURLs?: string[];
  failureReason?: string;
  failureNotes?: string;
  resolutionDate?: Timestamp;
  resolutionDetails?: string;

  // Bookkeeping. Labels are only soft-deleted
  createdAt?: string | FieldValue;
  updatedAt?: string | FieldValue;
  deletedAt?: string | FieldValue; // nullable for non-deleted labels

  // CSV Ingestion bookkeeping. ID built from (epochms + csv row #)
  ingestionId?: string;

  // Unique tracking slug
  trackingNumber?: string;

  // Onfleet tracking url
  trackingUrl?: string;

  // ID that maps to onfleet's system
  taskId?: string;

  // Unique ID representing the delivery address
  locationId?: string;

  // Is signature required for shipment
  signatureRequired?: boolean;

  // Sort code to place on label if needed by provider
  sortCode?: string;

  // Label state
  state?: TrackingEventState;

  // The current status
  csvProcessingStatus: CSVProcessingStatus;

  // Error message, in case we had an error
  errorMessage?: string;

  stringDistance?: number;

  // The lookup code is the current # of labels created for the day so far.
  lookupCode?: number;

  // Route information associated with the task. Only gets set on task assignment webhook.
  routeInformation?: ILabelRouteInformation;

  // Only applies to orders that came from Shopify.
  shopifyOrderId?: string;
  shopifyOrderNumber?: string;

  // Ship date when the label was created. Cannot be modified. Format is YYYY-MM-DD
  originalShipDate?: string;

  // Date when label will be shipped out. Format is YYYY-MM-DD
  shipDate?: string;

  // Flag whether or not a created label is using the legacy CSV flow
  legacyCsv?: boolean;

  // Counter for the number of redelivery attempts
  redeliveryAttempts?: number;

  // Flag whether or not this label has been reviewed and approved by Ops. Label should not be synced to OnFleet unless flag is true
  manuallyApproved?: boolean;

  // Id of the associated crossdock (ICrossdockOrganization)
  associatedCrossdockId?: string;
}

export interface ILabelRouteInformation {
  team?: string;
  workerId?: string;
  driverName?: string;
  assigned: boolean;
}

/**
 * Contains the base properties of the IRecipient
 * If you want to modify the IRecipient, please modify this interface
 */
interface IRecipientBase {
  firstName: string;
  lastName?: string;
  address: IAddress;
  validatedAddress?: IValidatedAddress;
  validatedAddressV2?: IValidatedAddressV2;
  /** @deprecated we use the geocode in validatedAddressV2 instead */
  location?: ICoordinate;
}

/**
 * Extends IRecipientBase with a required phone number and optional email
 * Please modify IRecipientBase for shared properties
 */
interface IRecipientPhone extends IRecipientBase {
  phone: string;
  email?: string;
}

/**
 * Extends IRecipientBase with a required email and optional phone number
 * Please modify IRecipientBase for shared properties
 */
interface IRecipientEmail extends IRecipientBase {
  phone?: string;
  email: string;
}

/**
 * IRecipient is a union of IRecipientEmail and IRecipientPhone
 * This allow us to require a phone number OR email (or possibly both)
 */
export type IRecipient = IRecipientEmail | IRecipientPhone;

export interface IAddress {
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
}

export interface IRate {
  packageType: PackageType;
  packageTypeOverride?: PackageType;
  serviceType: ServiceType;
  currency: Currency;
  deliveryPrice: Dinero;
  signaturePrice: Dinero;
  totalPrice?: Dinero;
  price?: Dinero;
  options?: Array<IRateOptions>;
  optionsTotalPrice?: Dinero;
}

/**
 * Not every customer has a different rate for different package size. We go
 * with default option and if the default isn't provided then we go bottom up
 * (check large, then check medium, then check small)
 */
export enum PackageType {
  DEFAULT = "DEFAULT",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  EXTRA_LARGE = "EXTRA_LARGE",
}

export enum ServiceType {
  SAME_DAY = "SAMEDAY", // The default option
  NEXT_DAY = "NEXTDAY",
}

export enum LabelCreationMethod {
  API = "API",
  FTP = "FTP",
  SHOPIFY = "SHOPIFY",
  MERCHANT_DASHBOARD = "MERCHANT_DASHBOARD",
  OPERATIONS_DASHBOARD = "OPERATIONS_DASHBOARD",
}
/**
 * @deprecated We longer use this field for CSVs
 */
export enum CSVProcessingStatus {
  INCOMPLETE = "INCOMPLETE", // The uploaded CSV row has incomplete information. Will not be proccesed
  PENDING = "PENDING", // The doc was just created in firestore. We haven't done anything with it yet
  RETRYING = "RETRYING", // A document can be flipped to 'retrying' to trigger a retry
  PROCESSING = "PROCESSING", // We are running validations and are actively handling the request
  COMPLETED = "COMPLETED", // The request was successfully completed
  FAILED = "FAILED",
}

export interface IRateOptions {
  name: string;
  price: Dinero;
}

/**
 * Smarty Streets precision options
 * Metric from SS to estimate the accuracy of either the geocode lookup or the address validation.
 * These are ordered by accuracy, for example, "Manual" and "DeliveryPoint" are the most accurate and "None" is least accurate.
 * See https://smartystreets.com/docs/cloud/international-street-api for more details.
 * Option "Manual" is for manual geocodes from the dashboard. These are assumed to be highest accuracy because
 * we expect ops to fill in the correct geocode here.
 */
export enum ISmartyStreetsPrecision {
  MANUAL = "Manual",
  DELIVERY_POINT = "DeliveryPoint",
  PREMISE = "Premise",
  THOROUGHFARE = "Thoroughfare",
  LOCALITY = "Locality",
  ADMINISTRATIVE_AREA = "AdministrativeArea",
  NONE = "None",
}

/**
 * We have multiple validation engine options. This enum is collection of them.
 */
export enum ValidationEngine {
  SMARTY = "SMARTY",
  GOOGLE = "GOOGLE",
}

/**
 * Interface for the version 2 validated address
 * line2 - apartment number
 * label - the full, validated address in one line
 * addressPrecision - smarty streets estimate of the accuracy of their address lookup
 * geocodePrecision - smarty streets estimate of the accuracy of their geocode lookup
 */
export interface IValidatedAddressV2 {
  country: string;
  province: string;
  city: string;
  postalCode: string;
  buildingNumber: string;
  street: string;
  line2?: string;
  label: string;
  geocode?: ICoordinate;
  geohash?: string;
  geocodePrecision?: ISmartyStreetsPrecision;
  addressPrecision: ISmartyStreetsPrecision;
  validationSource: ValidationEngine;
  taxRegionId?: string;
  isCommercial?: boolean;
}

export interface ITaxRegion {
  /** Firestore document id */
  id: string;

  /** Human readable unique identifier */
  slug: string;

  /** Tax rate in basis points (1/100 of a percent) e.g. 1050 for 10.5% */
  taxRate: number;

  /** Bookkeeping */
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
  deletedAt?: string | FieldValue;
}

/**
 * @deprecated We no longer use IValidatedAddress. Use IValidatedAddressV2 instead.
 */
export interface IValidatedAddress {
  Id: string;
  DomesticId: string;
  Language: LANGUAGE;
  LanguageAlternatives: string;
  Department: string;
  Company: string;
  SubBuilding: string;
  BuildingNumber: string;
  BuildingName: string;
  SecondaryStreet: string;
  Street: string;
  Block: string;
  Neighbourhood: string;
  District: string;
  City: string;
  Line1: string;
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  AdminAreaName: string;
  AdminAreaCode: string;
  Province: string;
  ProvinceName: string;
  ProvinceCode: string;
  PostalCode: string;
  CountryName: string;
  CountryIso2: string;
  CountryIso3: string;
  CountryIsoNumber: string;
  SortingNumber1: string;
  SortingNumber2: string;
  Barcode: string;
  POBoxNumber: string;
  Label: string;
  Type: string;
  DataLevel: string;
  AcRbdi: string;
  AcMua: string;
}

export enum LANGUAGE {
  ENGLISH = "ENG",
  FRENCH = "FRE",
}

/**
 * OnFleet task creation error interface.
 */
export interface IOnFleetError {
  error: {
    error: number; // error code, ref: https://gist.github.com/euskode/8a34b2152cb35ddeb659
    message: string;
    statusCode: number;
    cause?: any | null;
  };
  task: {
    notes: string;
    recipientName: string;
    recipientNotes: string;
    recipientSkipSMSNotifications: any;
    container: any;
    destination: any;
    recipients: any[];
    metadata: any;
  };
}

/**
 * Enum to keep track of the batch state. Possible values:
 * ENQUEUED - means that batch is in the queue and waiting for the worker
 * PROCESSING - worker picked up the batch and is processing it (creating tasks, updating db, etc)
 * COMPLETED - batch completed successfully, tasks are created or already exist for all of the labels
 * COMPLETED_WITH_FAILURES - tasks for some of the labels are created successfully, but for some
 * of them we had a errorresponse from the OnFleet (field related errors, validations, etc).
 * FAILED - worker function call errored out for some reason
 */
export enum IBatchState {
  ENQUEUED = "ENQUEUED",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  COMPLETED_WITH_FAILURES = "COMPLETED_WITH_FAILURES",
  FAILED = "FAILED",
}

/**
 * Interface for the OnFleet task creation batches.
 * id - unique identifier of the batch
 * groupId - id which is shared between batches that belong to the same group. It is
 * mainly used by the UI for querying the data for batches and displaying it.
 * cloudTaskId - each batch is assigned a queue task. This is the id of that task.
 * organizationSlugs - array of organizations which has labels in this batch.
 * labelIds - ids of the labels in this batch.
 * onFleetTaskIds - ids of the tasks created for the labels in this batch
 * skippedLabelIds - ids of the labels which were skipped during task creation process
 * For example, handler function skips the labels which already have a task assigned
 * failedTasks - array errors from the OnFleet.
 * shipDate - ship date of the labels in this batch
 * state - current status of the batch. See IBatchState enum for more details.
 */
export interface IBatch {
  id: string;
  groupId: string;
  cloudTaskId: string;
  organizationSlugs: string[];
  labelIds: string[];
  onFleetTaskIds: string[];
  skippedLabelIds: string[];
  failedTasks: IOnFleetError[];
  shipDate: string;
  state: IBatchState;
  createdAt?: string | FieldValue;
  updatedAt?: string | FieldValue;
  deletedAt?: string | FieldValue;
}

/* =================== FSA Related =================== */

export interface IFSA {
  id?: string;
  fsa: string; // The name of the FSA. Eg: 90210 or L9T
  dispatcher: string; // Who dispatches for this FSA?
}

// We assign dispatchers by FSAs (eg: L9T is dispatched by DSP1). However, this allows us to override an FSA for this specific org
export interface IFSAOverride {
  id: string;

  // Who are we overriding for. (Eg: KOTN)
  organization: string;

  // The overridden dispatcher
  dispatcher: string;
}

export enum ApiTokenType {
  LIVE = "LIVE",
  SANDBOX = "SANDBOX",
}
export interface IApiToken {
  // ID of the given token, provided in the X-Swyft-Access-Token header (UUID)
  id: string;

  // The ID of the organization that the token relates to
  organizationId: string;

  // The ID of the merchant that the token relates to
  merchantId?: string;

  // The ID of the DSP that the token relates to
  dspId?: string;

  // The api token type e.g. LIVE or SANDBOX
  type?: ApiTokenType;
}

export enum TrackingEventState {
  /** Swyft created label */
  PENDING = "PENDING",
  /** OnFleet task created (updates label via webhook) */
  CONFIRMED = "CONFIRMED",
  /** OnFleet task assigned to driver (updates label via webhook) */
  ASSIGNED = "ASSIGNED",
  /** Swyft scans the label at the warehouse */
  RECEIVED = "RECEIVED",
  /** OnFleet task started (initiated by the driver) */
  IN_TRANSIT = "IN_TRANSIT",
  /** OnFleet geolocation trigger (driver is near destination) */
  ARRIVING = "ARRIVING",
  /** OnFleet completes task (driver completes the task) */
  DELIVERED = "DELIVERED",
  /** OnFleet fails task (driver fails delivery) */
  FAILED = "FAILED",
  /** Swyft deletes the label */
  DELETED = "DELETED",
  /** If the label.redeliveryAttempts is less than 3, it will be put into this temporary state */
  SCHEDULED_FOR_REDELIVERY = "SCHEDULED_FOR_REDELIVERY",
  /** If the label.redeliveryAttempts is greater than 3, it will be put into this temporary state */
  RETURNING_TO_SENDER = "RETURNING_TO_SENDER",
  /** The package has been returned to sender*/
  RETURNED_TO_SENDER = "RETURNED_TO_SENDER",
  /** The package was not received from the merchant */
  NOT_RECEIVED = "NOT_RECEIVED",
  /** There was an issue with the package and it is being triaged */
  PROBLEM = "PROBLEM",
  /** The package is in storage and will be sent out at a later date */
  STORAGE = "STORAGE",
}

export interface ITrackingEvent {
  // The id of the event
  id?: string;

  // Source
  source?: ITrackingEventSource;

  // The state of the tracking event, eg ARRIVING
  state: TrackingEventState;

  // String describing the event, intended for user display
  details: string;

  /**
   * When the event occurred
   * @todo: we overload timestamp to do multiple things.  We should have
   * different variables.
   */
  timestamp?: string | FieldValue | Timestamp;

  /** failure code for the failed delivery attempts */
  failureCode?: DeliveryErrorCode;
}

export interface ITrackingEventSource {
  type: ITrackingEventSourceType;
  metadata?: string; // JSON stringified
}

export enum ITrackingEventSourceType {
  DASHBOARD = "DASHBOARD",
  SWYFT = "SWYFT",
  ONFLEET = "ONFLEET",
}

export interface ITracking extends ILabel {
  status: ITrackingEvent;
  statusHistory: ITrackingEvent[];
}

export interface ITrackingResponse {
  /** Current label status */
  currentStatus: ITrackingEvent;

  /** List of tracking events */
  trackingHistory: ITrackingEvent[];

  /** Tracking number of label associated with this response */
  trackingNumber: string;

  /** Shortened name of the customer (First name + first letter of last name) */
  shortName?: string;

  /** City of the origin address */
  originCity?: string;

  /** Province of the origin address */
  originProvince?: string;

  /** Country of the origin address */
  originCountry?: string;

  /** City of the destination address */
  destinationCity?: string;

  /** Province of the destination address */
  destinationProvince?: string;

  /** Country of the destination address */
  destinationCountry?: string;

  /** Country of the destination address */
  destinationTimezone: string;

  /** Date when label will be shipped out. Format is YYYY-MM-DD */
  shipDate?: string;

  /** Counter for the number of redelivery attempts */
  redeliveryAttempts?: number;

  /** Proof of delivery */
  photoURLs?: string[];

  /** Business name for this label */
  businessName: string;

  /** Merchant slug for this label */
  merchantSlug: string;

  /** Date when label will be expected for delivery. Format is YYYY-MM-DD */
  expectedDeliveryDate?: string;

  /** Boolean flag determining if the package was received at the warehouse */
  received: boolean;
}

/**
 * This interface represents the CSV template that merchants upload
 */
export interface ICSVRow {
  // For bookkeeping. Zero padded row number
  rowId: string;

  // Recipient
  Recipient_Name: string;
  Recipient_First_Name?: string;
  Recipient_Last_Name?: string;
  Organization_Slug?: string;
  Address_Line1: string;
  Address_Line2?: string;
  "City/Town": string;
  "State/Province": string;
  Postal_Code: string;
  Country: string;
  Recipient_Notes?: string;
  Recipient_Phone: string;

  // Merchant
  // We get the name from the organization
  Merchant_Name: string;
  Merchant_Line1: string;
  Merchant_Line2: string;
  Merchant_City: string;
  Merchant_Postal_Code: string;
  Merchant_Province: string;
  Merchant_Country: string;

  Notification: string;

  "Package_Length (inches)": string;
  "Package_Width (inches)": string;
  "Package_Height (inches)": string;
  "Package_Weight (lb)": string;
  Package_Type: PackageType;
  Service_Type: ServiceType;

  Error_Type: string;
  Resolution: string;
  Resolution_Date: string;

  Tracking_Number?: string;
  Task_Details?: string;

  retries: number;
  status: string;
  timestamp: string;
  shopifyOrderId?: string;
  orderNumber?: string;
}

/* =================== Environment Related =================== */

export interface IEnvironment {
  production: boolean;
  backend: string;
  trackingURL: string;
  retoolIntegrationSecret: string;
  firebase: IFirebaseEnvironment;
  firestorage: IFirestorageEnviroment;
  firestore: IFirestoreEnvironment;
  functions: IFunctionsEnvironment;
  smartyStreets: ISmartyStreetsEnvironment;
  shipstation: IShipstationEnvironment;
  twilio: ITwilioEnvironment;
  elasticAppSearch: IElasticAppSearchEnvironment;
  slackTrackingBot: ISlackBotCredentials;
  sendgrid: ISendgridEnvironment;
  logisticsos: ILogisticsOSEnvironment;
  gcp: IGCPEnvironment;
}

export interface ICredentials {
  email: string;
  password: string;
  code?: string;
}

export interface IFirebaseEnvironment {
  apiKey: string;
  appId: string;
  authDomain: string;
  databaseURL: string;
  messagingSenderId: string;
  projectId: string;
  storageBucket: string;
}

export interface IFirestorageEnviroment {
  csvupload: string;
}

export interface IFirestoreEnvironment {
  codes: string;
  labels: string;
  organizations: string;
  tracking: string;
  users: string;
  tasks: string;
  transactions: string;
  apiTokens: string;
  eventHistory: string;
  dataWarnings: string;
  SMSLogs: string;
  deliveryAttempts: string;
  deliveryServicePartners: string;
  drivers: string;
  taxRegions: string;
}

export interface IFunctionsEnvironment {
  url: string;
  eastUrl: string;
}

export interface ISmartyStreetsEnvironment {
  authId: string;
  authToken: string;
}

export interface ISendgridEnvironment {
  apiKey: string;
}

export interface ITwilioEnvironment {
  activeNumber: string;
  sid: string;
  authToken: string;
}

export interface IShipstationEnvironment {
  baseUrl: string;
}

export interface IElasticAppSearchEnvironment {
  apiKey: string; // API access key that can read and write against all available API endpoints
  readOnlyKey: string; // API reading key that has read-only 'search' access to the Search endpoint
  baseUrl: string;
  engineName: string;
}

export interface ILogisticsOSEnvironment {
  apiKey: string;
  authHeader: string;
  baseUrl: string;
}

export interface ISlackBotCredentials {
  slackBotToken: string;
  slackSigningSecret: string;
}

export interface IShipstationConfig {
  id?: string;

  /** The ID of the associated merchant */
  merchantId: string;

  /** The API Key from ShipStation settings */
  apiKey?: string;

  /** The API Secret from ShipStation settings */
  apiSecret?: string;

  /** The ID of the "swyft" tag in ShipStation */
  tagId?: string;

  /** The ID of the registered ShipStation webhook */
  webhookId?: string;

  /** Is shopify merchant? */
  shopify?: boolean;

  /** When a label is printed, should it mark the ShipStation order as shipped */
  autoFulfillmentEnabled?: boolean;

  /** Should the ShipStation orders be tagged with Swyft */
  shouldTagOrders?: boolean;
}

export interface IGCPEnvironment {
  apiKeys: {
    geocoding: string;
  };
}

export interface IOrder {
  orderId: number;
  orderNumber: string;
  requestedShippingService: string;
  shipTo: { postalCode: string };
}

export interface IEvent {
  docId: string;
  docPath: string; // eg: labels/label_id, organizations/org_id/labels/label_id
  type: IEventType;
  timestamp?: string | FieldValue;
  metadata: any;
}

export enum IEventType {
  CREATE = "CREATE",
  DELETE = "DELETE",
  UPDATE = "UPDATE",
  UPDATE_SHIP_DATE = "UPDATE_SHIP_DATE",
  ADDRESS_VERIFICATION = "ADDRESS_VERIFICATION",
  ADDRESS_MANUAL_APPROVAL = "ADDRESS_MANUAL_APPROVAL",
  SYNC_TO_ONFLEET = "SYNC_TO_ONFLEET",
  UNASSIGN_ONFLEET_TASK = "UNASSIGN_ONFLEET_TASK",
  RESCHEDULE_ONFLEET_TASK = "RESCHEDULE_ONFLEET_TASK",
  RETURN_TO_MERCHANT = "RETURN_TO_MERCHANT",
}

/* =================== ODL Live Related =================== */

export interface ITask {
  // Task unique identifier
  id: string;

  // Pickup or Dropoff
  type: ITaskType;

  // Associated label ID
  labelId?: string;

  // Represents the state of the task
  state: ITaskState;

  // When the task was created
  createdAt: string;

  // When the task was updated (OnFleet updates task information)
  updatedAt: string;

  // Only applies to Pickup tasks
  pickupTime: string;

  // When the task should be completed
  finishBeforeTime: string;

  // Number of packages in the task. Can be updated if tasks are consolidated.
  packageCount: number;

  // Origin
  origin: IRecipient;

  // Destination
  destination: IRecipient;

  // Organization slug (needed for onFleet task creation)
  organizationSlug: string;

  // Notes:
  notes?: string;

  // The associated onFleetTaskId.
  // Note - Tasks can have the SAME onFleetTask Id (duplicate pickups).
  onFleetTaskId?: string;
}

export interface ICoordinate {
  latitude: number;
  longitude: number;
}

export interface IOrganizationPickupDetails {
  organizationSlug: string;
  quantity: number;
}

export enum ITaskType {
  PICKUP = "PICKUP",
  DROPOFF = "DROPOFF",
}

export enum ITaskState {
  PENDING = "PENDING",
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

/* =================== MISC. =================== */

export interface IDataWarning {
  docID: string;
  type: IDataWarningType;
  createdAt: string | FieldValue;
}

export enum IDataWarningType {
  MISSING_PHONE_NUMBER = "MISSING_PHONE_NUMBER",
  DESTINATION_OUTSIDE_FSA = "DESTINATION_OUTSIDE_FSA",
  DUPLICATE_LABEL = "DUPLICATE_LABEL",
}

/** Stores onfleet team information */
export interface IOnfleetTeam {
  /** ID of the team in onfleet */
  id: string;
  /** Name of the team in onfleet */
  name: string;
}

/** Team information from onfleet for a given crossdock */
export interface IOnfleetTeams {
  /** Team info for intake orders */
  intake: IOnfleetTeam;
  /** Team info for redelivery orders */
  redeliveries: IOnfleetTeam;
  /** Team info for RTS orders */
  returnToSender: IOnfleetTeam;
  /** Team where routes are stored */
  routes: IOnfleetTeam;
}

/**
 * An interface for the sortation centers/crossdocks. This is where merchants will have
 * their pickups arrive. Crossdocks service a geographical area represented by FSA's.
 * The Merchant's assigned crossdock determines which area is covered by same-day shipping.
 */
export interface ICrossdock {
  /** unique database id/key of the object */
  id: string;
  /** display name of the organization */
  name: string;
  /** contact and address information */
  contact: IRecipient;
  /** array of service area codes (for example: ZIP, FSA) */
  coverageAreas: string[];
  /** OnFleet team information for the given crossdock */
  onfleetTeams: IOnfleetTeams;
  /** Id of the onfleet organization the crossdock operates in */
  onfleetOrganizationId: string;
  /** timezone in TZ Database format, ref: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones */
  timezone: string;
  /** SLA Cutoff-time - latest delivery time for a package to incurr a billable event */
  SLACutoffTime?: string;
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
  deletedAt?: string | FieldValue;
}

export interface ICreateCrossdockRequest {
  /** display name of the organization */
  name: string;
  /** array of service area codes (for example: ZIP, FSA) */
  coverageAreas: string[];
  /** contact and address information for the crossdock */
  contact: IRecipient;
  /** timezone the crossdock operates in TZ Database format */
  timezone: string;
  /** SLA Cutoff-time - latest delivery time for a package to incurr a billable event */
  SLACutoffTime?: string;
  /** Onfleet team information for the given crossdock */
  onfleetTeams: IOnfleetTeams;
  /** Id of the onfleet organization the crossdock operates in */
  onfleetOrganizationId: string;
}

export enum Currencies {
  CAD = "CAD",
  USD = "USD",
}

/** Delivery service add ons supported by us */
export enum ServiceAddOns {
  SIGNATURE = "SIGNATURE",
  CHECK_ID = "CHECK_ID",
  XL_PACKAGE = "XL_PACKAGE",
  ADDRESS_CORRECTION = "ADDRESS_CORRECTION",
  RELABELLING_FEE = "RELABELLING_FEE",
  WAITING_FEE = "WAITING_FEE",
}

export enum RateCardType {
  rateCard = 'rateCard',
  rateCardV2 = 'rateCardV2',
}

export type IRateCardAddOns = {
  [key in ServiceAddOns]?: number;
};

export type IPackageTypeRate = {
  [key in PackageType]?: number;
};

export type IRateCardServices = {
  [key in ServiceType]: IPackageTypeRate;
};

export interface IRateCard {
  currency: Currencies;
  defaultPackageType: PackageType;
  addOns?: IRateCardAddOns;
  services: IRateCardServices;
  /** Rates for redelivery attempts */
  redeliveryRate: IPackageTypeRate;
  allowedDeliveryAttempts: number;
  isRateShopper?: boolean;
}

export type IRateCardV2Entry = {
  minWeight: number;
  maxWeight: number;
  serviceType: ServiceType[];
  rate: number;
  redeliveryRate: number;
};

export type IRateCardV2 = {
  currency: Currencies;
  entries?: IRateCardV2Entry[];
  addOns?: IRateCardAddOns;
  allowedDeliveryAttempts: number;
};

export interface IMerchantRateCard {
  /** Unique identifier */
  id: string;
  /** Parent merchant ID */
  merchantId: string;
  /** Currency for this Ratecard */
  currency: Currencies;
  /** Default package type for this merchant */
  defaultPackageType: PackageType;
  /** Additional add-on services rates */
  addOns?: IRateCardAddOns;
  /** Rates by service and by size */
  services: IRateCardServices;
  /** Number of redeliveries allowed for this merchant */
  allowedDeliveryAttempts: number;
  /** Rates for redelivery attempts */
  redeliveryRate: IPackageTypeRate;
  /** Custom flag used by freightcom */
  isRateShopper?: boolean;
  /** The time which this rate card comes into effect */
  activeFrom: FieldValue | Timestamp;
  /** Bookkeeping fields */
  createdAt: FieldValue | Timestamp;
  updatedAt: FieldValue | Timestamp;
  deletedAt?: FieldValue | Timestamp;
}

export interface IMerchant {
  id: string;
  slug: string;
  name: string;
  contact: IRecipient;
  rateCard: IRateCard;
  pickUpTime?: string;
  pickUpNotes?: string;
  smsConfiguration: ISMSConfiguration;
  createdAt?: string | FieldValue;
  updatedAt?: string | FieldValue;
  deletedAt?: string | FieldValue;
  merchantType: IMerchantType;
  webhooks?: IMerchantWebhooks;
  integrations?: MerchantIntegration[];

  /** Sales Force Id added to createMerchantQuery in reTool */
  /** https://useswyft.retool.com/editor/0f30a100-aba9-11eb-af43-db7198a8c925/Merchant%20Onboarding#merchantRowIndex=0 */
  salesForceId?: string;

  /** Elastic App Search read-only api search key */
  searchToken?: string;

  /** Configuration specific to merchants using Shopify */
  shopifyConfig?: IShopifyConfig;

  /** Configuration specific to merchants using ShipStation */
  shipStationConfig?: IShipstationConfig;

  /** Flag to mark merchant as active or inactive in our system. If inactive, merchant won't be able to create labels. */
  isActive?: boolean;

  /** Merchant Data Studio Report Embeddable URL  */
  reportURL?: string;

  /** Custom label configuration */
  labelConfiguration?: {
    labelText?: string;
  };

  activeRateCard?: RateCardType;
  /** Merchant-V2 Fields */
  // @deprecated 
  isV2Merchant?: boolean;
  rateCardV2?: IRateCardV2;
  // @deprecated 
  monthlyVolume?: MonthlyVolume;
  // @deprecated
  packageWeight?: PackageWeight;
  // @deprecated
  packageSize?: PackageSize;
  countryCode?: CountryISO2;
  companyName?: string;
  zones?: {
    /** key is zone doc id  */
    [key: string]: {
      status: ZoneStatus;
    };
  };
}

export enum ZoneStatus {
  ACTIVE = "ACTIVE",
  PENDING = "PENDING",
}

export enum PackageWeight {
  VERY_LIGHT = "VERY_LIGHT",
  LIGHT = "LIGHT",
  MEDIUM = "MEDIUM",
  HEAVY = "HEAVY",
  V1_HEAVY = "V1_HEAVY",
  V2_HEAVY = "V2_HEAVY",
  V3_HEAVY = "V3_HEAVY",
  V4_HEAVY = "V4_HEAVY",
}

export enum MonthlyVolume {
  VERY_SMALL = "VERY_SMALL", // up to 1000
  SMALL = "SMALL", // 1000 to 5000
  MEDIUM = "MEDIUM", // 5000 - 25k
  LARGE = "LARGE", // 25k to 100k
  EXTRA_LARGE = "EXTRA_LARGE", //  100K +
}

export enum PackageSize {
  BAG = "BAG",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  EXTRA_LARGE = "EXTRA_LARGE",
}

export interface IShopifyConfig {
  /** Shopify store url, eg: kotn-ss15.myshopify.com */
  shopName: string;

  /** Swyft checkout visible to customers */
  checkoutEnabled: boolean;

  /** When carrier webhook is setup, it is created with a unique ID */
  carrierId: number;

  /** Set to false for merchants who use 3rd party integration to manage orders */
  createLabelOnOrderCreation: boolean;

  /** Price surfaced to customer */
  shippingPriceCents: number;

  /**
   * Discount settings which indicate the type of discount (Flat, percentage)
   * and the amount of discount to apply
   */
  cartValueDiscountSettings?: IShopifyDiscountSettings;

  /** Temporary Shopify token storage until app is verified */
  key: string;

  /** Temporary Shopify token storage until app is verified */
  secretKey: string;

  /** If merchant uses a 3PL with an API integration, we should ignore orders created through shopify */
  ignoreOrderWebhooks: boolean;

  /** Custom shipping lines & free shpping prefix text are available for the merchant */
  customShippingLinesEnabled: boolean;

  customShippingLines: {
    beforeCutoff: IShippingLine;
    afterCutoff: IShippingLine;
    /** Custom prefix text shown when shipping is free */
    freeShippingPrefix?: string;
    /** Custom prefix text shown when shipping is not free */
    shippingPrefix?: string;
  };

  /**
   * @deprecated - Use customShippingLines.freeShippingPrefix
   * */
  customFreeShippingPrefix?: string;

  /** Auto fulfill shopify orders */
  autoFulfillmentEnabled: boolean;

  /** Should we check the line items against the excluded items sub-collection */
  excludedItemsEnabled?: boolean;

  /** Should we check the address against the excluded addresses sub-collection */
  excludedAddressesEnabled?: boolean;

  /** The phoneRequired field when returning rates. Defaults to true. */
  phoneRequired?: boolean;

  /** Cutoff Time for Shopify checkout */
  customerCutoffTime: string;
}

/**
 * Specifies the type of discount to apply if enabled.
 * cartValueFlatDiscount (FLAT) or cartValueDiscountPercent (PERCENTAGE)
 * */
export enum IShopifyDiscountType {
  FLAT = "FLAT",
  PERCENTAGE = "PERCENTAGE",
}

/**
 * Discount settings for a shopify merchant
 */
export interface IShopifyDiscountSettings {
  // Enable or Disable discount settings
  discountEnabled: boolean;

  // The cart value required in order for discount to be applied
  cartValueThresholdCents: number;

  // The type of discount to apply
  discountType: IShopifyDiscountType;

  /**
   * Generic field to represent the discount to apply to shipping price.
   * The type of this discount is determined by discountType
   *
   * Can either be a percentage [0-1] or a flat amount [0 - shippingPrice]
   * Flat discount amount, checkout price = shipping price - flat discount
   * Discount percent, checkout price = shipping price - (shipping price * discount percent) */
  discountAmount: number;
}

export interface IShippingLine {
  name: string;
  description: string;
}

/** Merchant types */
export enum IMerchantType {
  ENTERPRISE = "ENTERPRISE",
  SMB = "SMB",
  /** The customers that pick and pack items for merchants */
  THIRD_PARTY_LOGISTICS = "THIRD_PARTY_LOGISTICS",
  /** The customers of the 3PL */
  THIRD_PARTY_LOGISTICS_MERCHANT = "THIRD_PARTY_LOGISTICS_MERCHANT",
}

export interface IRetoolCreateMerchantRequest {
  slug: string;
  name: string;
  contact: IRecipient;
  rateCard: IRateCard;
  crossdockIds: string[];
  cutoffTime: string;
  processingDays: number;
  pickUpTime?: string;
  pickUpNotes?: string;
  merchantType: IMerchantType;
  salesForceId: string;
}

export enum DATE_FORMAT {
  /** Formatted date example: 2020-12-31 */
  SHIP_DATE = "YYYY-MM-DD",
  /** Formatted date example: Apr 21, 2021 */
  READABLE_SHIP_DATE = "MMM DD, YYYY",
  /** Formatted date example: 09:30 AM */
  CUT_OFF_TIME = "hh:mm A",
  /** Formatted date example: April 21st 2021 */
  LABEL_PDF = "MMMM Do YYYY",
  /** Formatted date example: 9:30 AM, 22 Apr 2021 */
  READABLE_TIMESTAMP = "h:mm A, DD MMM YYYY",
  /** Formatted date example: 09:30 AM */
  ROUTING_ENGINE = "hh:mm A",
}

/** Package dimensions. */
export type IDims = {
  length: number;
  width: number;
  height: number;
  weight: number;
};

/** New label model. Used with merchants model. */
export interface ILabelV2 {
  /** xid generated by application */
  id: string;

  /** ID of the merchant that owns the label */
  merchantId: string;

  /** Human readable merchant identifier */
  merchantSlug: string;

  /** Name of the business shipping the package. */
  businessName: string;

  /** Package pickup location */
  origin: IRecipient;

  /** Customer drop-off location */
  destination: IRecipient;

  /** Notes for driver when delivering */
  notes?: string;

  /** Time when the Onfleet task was completed i.e. either delivered or failed */
  completionTime?: Timestamp;

  /**
   * POD photo URLs from Onfleet
   * @deprecated Please use IDeliveryAttempt photoURLs instead
   * */
  photoURLs?: string[];

  /** Failure reason for the delivery task */
  failureReason?: string;

  /** Driver inputted failure notes for the delivery task */
  failureNotes?: string;

  /** Resolution timestamp */
  resolutionDate?: Timestamp;

  /** Resolution details */
  resolutionDetails?: string;

  /** CSV Ingestion bookkeeping. ID built from (epochms + csv row #) */
  ingestionId?: string;

  /** Unique tracking slug */
  trackingNumber: string;

  /** Onfleet tracking url */
  onfleetTrackingUrl?: string;

  /** ID that maps to onfleet's system */
  onfleetTaskId?: string;

  /** Label state */
  state: TrackingEventState;

  /** Route information associated with the task. Only gets set on task assignment webhook. */
  routeInformation?: ILabelRouteInformation;

  /** Only applies to orders that came from Shopify. */
  shopifyOrderId?: string;

  /** Only applies to orders that came from Shopify. */
  shopifyOrderNumber?: string;

  /** External order identifier provided by merchant. */
  orderNumber?: string;

  /** External reference string provided by customer */
  reference?: string;

  /** External reference string provided by customer */
  reference2?: string;

  /** Ship date when the label was created. Cannot be modified. Format is YYYY-MM-DD */
  originalShipDate: string;

  /** Date when label will be shipped out. Format is YYYY-MM-DD */
  shipDate: string;

  /** Counter for the number of redelivery attempts */
  redeliveryAttempts?: number;

  /** Flag whether or not this label has been reviewed and approved by Ops. Label should not be synced to OnFleet unless flag is true */
  manuallyApproved?: boolean;

  /** Id of the associated crossdock (ICrossdock) */
  associatedCrossdockId: string;

  /** Id of the pickup location for this label */
  pickupLocationId?: string;

  /** Id of the DSP which will handle this label (unmanaged flow) */
  dspId?: string;

  /** Weight and dimensions of the package provided by merchant */
  dims?: IDims;

  /** Type of the package derived from its dimensions  */
  packageType: PackageType;

  /** Provided service type by merchant */
  serviceType: ServiceType;

  /** List of service add ons (for ex. signature on delivery) requested by merchant */
  serviceAddOns?: ServiceAddOns[];

  /** Sort code to place on label if needed by provider */
  sortCode?: string;

  /**
   * Timestamp of the latest scan of this label
   * @deprecated Please use sub collection IScanEvents instead
   */
  scanned?: Timestamp;

  /** Timestamp of when the label was created */
  createdAt: string | FieldValue;

  /** Timestamp of when the label was last updated */
  updatedAt: string | FieldValue;

  /** Timestamp of when the label was marked as deleted. Labels are only soft-deleted */
  deletedAt?: string | FieldValue;

  /** Flag to determine if a label was received at a warehouse */
  received: boolean;

  /** Date when label will be expected for delivery. Format is YYYY-MM-DD */
  expectedDeliveryDate?: string;

  /** Method used to create the label */
  creationMethod?: LabelCreationMethod;

  /** Optional text rendered at the bottom of a label */
  labelText?: string;

  /** List of all DSPs that have scanned a label */
  scannedBy?: string[];

  /** Identification of the label at DSP side. Used for integration and cross reference between Swyft and DSPs */
  dspLabelReferenceId?: string;

  /** ZPL content for who provides their own label's layout */
  dspCustomZpl?: string;

  /** DSP integration logs recorded when communication from Sywft are sent to the DSP API and its response status */
  dspIntegrationLog?: [
    {
      status: "OK" | "ERROR";
      action: "REQUEST" | "MANIFEST";
      message?: string;
      timestamp: FieldValue;
    }
  ];

  /** Optional id for grouping labels from the merchant */
  shipmentId?: string;
}

export interface IPickupLocation {
  /** Firestore Id */
  id: string;

  /** Name for the pickup location */
  name?: string;

  /** Doc if of the zone associated to this pickup location */
  zone?: string;

  /** Parent merchant Id */
  merchantId: string;

  /** Human readable nickname */
  slug: string;

  /** Order cutoff time */
  cutoffTime: string;

  /** A processing time buffer for merchants to prepare orders */
  processingDays: number;

  /** An days array of eligible ship days (0 to 6 represent Sunday to Saturday) */
  eligibleShipDays: number[];

  /** Time for pickup HH:MM AM/PM */
  pickupTime?: string;

  /** Pick up instructions */
  pickupNotes?: string;

  /** Id of the associated crossdock for this pickup location */
  associatedCrossdockId: string;

  /** Recipient contact for this pickup location */
  contact: IRecipient & { title?: string };

  /** If DSP is set, labels created for the location will be auto assigned to the DSP */
  dspId?: string;

  /** Bookkeeping */
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
  deletedAt?: string | FieldValue;
}

/** Excluded Item is a subcollection under the merchant model. */
export interface IExcludedItem {
  /** Firestore Id */
  id: string;

  /** Parent merchant Id */
  merchantId: string;

  /** Unique identifier of the item on a per-merchant basis */
  productId: string;

  /** Used to identify products with variants in Shopify */
  variantId?: string;

  /** Bookkeeping */
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
}

/** New label request interface for requests from merchant dashboard. */
export interface ICreateLabelCallableMerchantRequest {
  firstName: string;
  lastName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  phoneNumber?: string;
  email?: string;
  dims?: IDims;
  notes?: string;
  orderNumber?: string;
  trackingNumber?: string;
  shipDate?: string;
  serviceType?: ServiceType;
  shopifyOrderId?: string;
  signatureRequired: boolean;
  creationMethod: LabelCreationMethod;
  shipmentId?: string;
}

/** New label request interface for requests from internal dashboard. */
export interface ICreateLabelCallableDashboardRequest
  extends ICreateLabelCallableMerchantRequest {
  slug: string;
}

export interface IMerchantGroup {
  id: string;
  name: string;
  merchantIds: string[];
  createdAt: string | FieldValue;
  updatedAt: string | FieldValue;
  deletedAt?: string | FieldValue;

  /** Merchant Group Data Studio Report Embeddable URL  */
  reportURL?: string;
}

export interface ICreateMerchantGroup {
  name: string;
  merchantIds: string[];
}

export interface ICreateMerchantGroupRetoolRequest {
  name: string;
  merchantIds: string[];
}

export interface ICreateExcludedItemRequest {
  merchantId: string;
  productId: string;
  variantId?: string;
}

export interface IBulkUpdateExcludedItemsPayload {
  merchantId: string;
  excludedItemsPayloadBody: IBulkUpdateExcludedItemBody[];
}
export interface IBulkUpdateExcludedItemBody {
  productId: string;
  variantId?: string;
}

export enum CountryISO2 {
  USA = "US",
  CANADA = "CA",
}

export const ISO2CodeToNamesMap: { [key: string]: Set<string> } = {
  [CountryISO2.USA]: new Set([
    "united states of america",
    "usa",
    "united states",
    "us",
  ]),
  [CountryISO2.CANADA]: new Set(["canada", "can", "ca"]),
};

/** Specifies different kinds of scans that can take place */
export enum IScanEventType {
  /** Package was scanned when scanner app is in received mode */
  ARRIVAL_SCAN = "ARRIVAL_SCAN",
  /** Package was scanned when scanner app is in sortation mode */
  SORT_SCAN = "SORT_SCAN",
  /** Package scanned in read only mode */
  READ_ONLY_SCAN = "READ_ONLY_SCAN",
  /** Package scanned by gatsby */
  GATSBY_SCAN = "GATSBY_SCAN",
  /** Package scanned by a driver */
  DRIVER_SCAN = "DRIVER_SCAN",
}

/** Subcollection that lives under the ILabelV2 representing a history of scan events on the label */
export interface IScanEvent {
  /** ID of the document */
  id: string;
  /** The type of scan */
  type: IScanEventType;
  /** Timestamp as to when the scan event occured */
  timestamp: FieldValue;
  /** Id of user that triggered the scan event */
  userId?: string;
  /** UserType of the user that triggered the scan event */
  userType?: UserType;
  /** Email of the user that triggered the scan event */
  userEmail?: string;
  /** ID of the DSP that triggered the scan event */
  dspId?: string;
}

/** Creates a scan event with the given request */
export type ICreateScanEventRequest = Pick<IScanEvent, "type">;

/** Specifications of different types of SMS events */
export enum SMSType {
  SCHEDULED_FOR_DELIVERY = "SCHEDULED_FOR_DELIVERY",
  NEXT_FOR_DELIVERY = "NEXT_FOR_DELIVERY",
  ARRIVING = "ARRIVING",
  DELIVERED = "DELIVERED",
  DELIVERED_SURVEY = "DELIVERED_SURVEY",
  FAILED = "FAILED",
  SCHEDULED_FOR_REDELIVERY = "SCHEDULED_FOR_REDELIVERY",
  RETURNING_TO_SENDER = "RETURNING_TO_SENDER",
  RECEIVED = "RECEIVED",
  ERROR = "ERROR",
}

export interface ISMSConfiguration {
  isEnabled: Boolean;
  templates: ISMSTemplate;
}

export interface ISMSTemplate {
  [SMSType.SCHEDULED_FOR_DELIVERY]: string;
  [SMSType.NEXT_FOR_DELIVERY]: string;
  [SMSType.ARRIVING]: string;
  [SMSType.DELIVERED]: string;
  [SMSType.DELIVERED_SURVEY]: string;
  [SMSType.FAILED]: string;
  [SMSType.SCHEDULED_FOR_REDELIVERY]: string;
  [SMSType.RETURNING_TO_SENDER]: string;
  [SMSType.RECEIVED]: string;
}

export interface IFailedSMSTemplate {
  [DeliveryErrorCode.NO_ACCESS_COMMERCIAL_ADDRESS]: string;
  [DeliveryErrorCode.NO_ACCESS_RESIDENTIAL_ADDRESS]: string;
  [DeliveryErrorCode.UNABLE_TO_COLLECT_SIGNATURE]: string;
  [DeliveryErrorCode.CUSTOMER_REFUSED_DELIVERY]: string;
  [DeliveryErrorCode.INCORRECT_CUSTOMER_ADDRESS]: string;
  [DeliveryErrorCode.MISSING_PACKAGE]: string;
  [DeliveryErrorCode.DRIVER_ACCIDENT]: string;
  [DeliveryErrorCode.ITEM_DAMAGED]: string;
  [DeliveryErrorCode.PAST_DELIVERY_WINDOW]: string;
  [DeliveryErrorCode.NO_SPACE_IN_CAR]: string;
  [DeliveryErrorCode.OTHER]: string;
}

export enum SMSTemplateTags {
  firstName = "{firstName}",
  businessName = "{businessName}",
  estimatedDeliveryTime = "{estimatedDeliveryTime}",
  trackingUrl = "{trackingUrl}",
  pastDeliveryAttemptDate = "{pastDeliveryAttemptDate}",
}

/** SMS events logging model */
export interface ISMSLog {
  /** Firestore Id of the document */
  id: string;

  /** Parent labelV2 id */
  labelId: string;

  /** Type of the notification corresponding to the delivery status */
  type: SMSType;

  /** Content of the notification */
  content: string;

  /** Twilio id of the SMS if it has been sent */
  twilioId?: string;

  /** Server timestamp of the moment in time when the entry is written to the database */
  createdAt: FieldValue;

  /** Server timestamp of the moment in time when the entry was updated */
  updatedAt?: FieldValue;
}

/**
 * Subcollection on the ILabelV2 that tracks when a task was completed or failed.
 */
export interface IDeliveryAttempt {
  id: string;
  /** Id of label */
  labelId: string;
  /** Id of the DSP from firestore - if not found, this will be "Unknown" */
  dspId: string;
  /** Name of the dsp deduced from firestore or onfleet */
  dspName: string;
  /** Id of driver from onfleet and corresponds to the id of the firestore document */
  driverId: string;
  /** Name of driver snapshotted onto the document */
  driverName: string;
  /** The date time the task was completed */
  completionTime: Timestamp;
  /** Boolean value indicating if it was a success or not from onfleet */
  success?: boolean;
  /** Unique identifier of the route. Generated on the fly with `${moment(completionTime).format('YYYY-MM-DD')}-${onfleetDriverId}` */
  routeId: string;
  /** For merchant billing - needed to track historical billing data */
  deliveryRate: IRateSnapshot;
  /** Notes from a successful delivery inputted by the driver */
  notes: string;
  /** Notes from a failed delivery when a driver selects "OTHER" as a failureReason */
  failureNotes?: string;
  /** Reason of failure. If it was a success, this will be "None" */
  failureReason: string;
  /** Error code of the failure. */
  errorCode?: DeliveryErrorCode;
  /** Error code of the failure from DSP system. */
  carrierErrorCode?: string;
  /** Related task Id from onfleet */
  onfleetTaskId: string;
  /** Short Id which is searchable in the onfleet dashboard */
  onfleetTaskShortId?: string;
  /** Vehicle type of the driver snapshotted onto the delivery attempt */
  vehicleType: VehicleType;
  /** Photos of driver delivery attempts */
  photoURLs?: string[];
  /** Total time traveled */
  totalTime?: number;
  /** Total distance traveled */
  totalDistance?: number;
  /** Datetime the document was created */
  createdAt: FieldValue | Timestamp;
  /** Datetime the document was last updated */
  updatedAt: FieldValue | Timestamp;
}

export interface IRateSnapshot {
  packageType: PackageType;
  currency: Currencies;
  deliveryPrice: number;
}

/** Interface to model DSPs in the db */
export interface IDeliveryServicePartner {
  /** Firestore generated Id of the document */
  id: string;
  /** Name of the DSP in kebab-case ex. "shark-logistics"*/
  name: string;
  /** When the document was created */
  createdAt: FieldValue;
  /** When the document was updated */
  updatedAt: FieldValue;
  /** IDs of the onfleet teams that this DSP uses */
  onfleetTeamIds?: string[];
  /** Boolean flag to indicate if DSP is using managed or unmanaged flow */
  isUnmanaged?: boolean;
  /** Reflects the number of drivers */
  fleetSize?: {
    rangeMin: number;
    rangeMax: number;
  };
  /** Driver and route management software used by DSP */
  dspSoftware?: DSPSoftware | string;
  /** Country ISO2 code where this DSP operates */
  country: CountryISO2;
  /** Contact and address information for DSP */
  contact?: IRecipient;
  /** Type of the DSP */
  type?: DSPType;
  /** Rates charged by this DSP for deliveries */
  rateCard?: IRateCardV2;
  /** Monthly volume DSP can deliver */
  monthlyVolume?: {
    rangeMin: number;
    rangeMax: number;
  };
  /** Display name of the company */
  companyName: string;
  /** Services supported by DSP */
  services?: DSPService[];
  /** Flag to mark DSP as active or inactive in our system. */
  isActive: boolean;
}

export interface IDSPCoverageArea {
  id: string;
  name: string;
  postalCodes: string[];
  rateCard?: IRateCardV2;
  service: DSPService;
  serviceLevel: ServiceType;
  country: CountryISO2;
  isActive: boolean;
  createdAt: FieldValue;
  updatedAt: FieldValue;
}

export interface IZone {
  id: string;
  name: string;
  originPostalCodes: string[];
  destinationPostalCodes: string[];
  minDeliveryDays: number;
  maxDeliveryDays: number;
  country: CountryISO2;
  coverageFileUrl: string;
  trailing90OTDUrl: string;
  createdAt: FieldValue;
  updatedAt: FieldValue;
}

export enum DSPType {
  LOCAL = "Local Courier",
  REGIONAL = "Regional Carrier",
  MAJOR = "Major Carrier",
}

export enum DSPService {
  PICKUP = "PICKUP",
  SORT = "SORT",
  DELIVERY = "DELIVERY",
  STORAGE = "STORAGE",
  MIDMILE = "MIDMILE",
}

export enum FleetSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  EXTRA_LARGE = "EXTRA_LARGE",
}

export enum DSPSoftware {
  XCELERATOR = "Xcelerator",
  CXT = "CXT",
  ECOURIER = "Ecourier",
  OTHER = "Other",
}

/** Interface of driver model */
export interface IDriver {
  /** Onfleet Id of the driver which also gets purposed as the firestore document Id */
  id: string;
  /** Name of the driver (from onfleet) */
  name: string;
  /** When the document was created */
  createdAt: FieldValue;
  /** When the document was last updated */
  updatedAt: FieldValue;
  /** Vehicle type (from onfleet) */
  vehicleType: VehicleType;
  /** Ids of the teams the driver belongs to in onfleet */
  onfleetTeamIds: string[];
}

/**
 * Vehicle our drivers use (inferred from onfleet)
 * See @onfleet/node-onfleet/Resources/Workers for more details
 */
export enum VehicleType {
  "CAR" = "CAR",
  "TRUCK" = "TRUCK",
  "UNKNOWN" = "UNKNOWN",
}

/** Request used to fetch a label for the scanner app */
export interface IGetLabelByScanRequest {
  trackingNumber?: string;
  orderNumber?: string;
  shopifyOrderId?: string;
}

/** Response payload of the received scan callable */
export interface IReceivedScanResponse {
  label: ILabelV2;
}

/** Onfleet failure reasons that drivers can choose when failing a task */
export enum OnfleetFailureCodes {
  OTHER = "OTHER",
  UNABLE_TO_LOCATE = "UNABLE_TO_LOCATE",
  ITEM_DAMAGED = "ITEM_DAMAGED",
  ITEM_INCORRECT = "ITEM_INCORRECT",
  UNAVAILABLE = "UNAVAILABLE",
  DESTINATION_INCORRECT = "DESTINATION_INCORRECT",
  DELIVERY_INCIDENT = "DELIVERY_INCIDENT",
}

/** Types of the service areas our system supports */
export enum ServiceAreaType {
  /** areas which we provide service */
  INCLUSION = "INCLUSION",
  /** areas inside inclusion areas which we do not provide service */
  EXCLUSION = "EXCLUSION",
}

/**
 * Interface which represents geographic an area a given crossdock provides service for.
 * A collection of service areas represent the total service area of a crossdock.
 */
export interface IServiceArea {
  /** firestore document id */
  id: string;
  /** id of the crossdock this service area belongs to */
  crossdockId: string;
  /** name of this area given by ops, increases readability */
  name: string;
  /** array of coordinates which define this bounds of this area */
  polygon: ICoordinate[];
  /** type of the area */
  type: ServiceAreaType;
  /** when the document was created */
  createdAt: FieldValue | Timestamp;
  /** when the document was last updated */
  updatedAt: FieldValue | Timestamp;
}

/** Request interface for creating a new service area for a given crossdock */
export interface ICreateServiceAreaRequest {
  /** id of the crossdock this service area belongs to */
  crossdockId: string;
  /** name of this area given by ops, increases readability */
  name: string;
  /** GeoJSON object of the geographic area */
  polygon: Polygon;
  /** type of the area */
  type: ServiceAreaType;
}

/** Internal error codes inputted by a driver through onFleet as part of notes section */
export enum DeliveryErrorCode {
  NO_ACCESS_COMMERCIAL_ADDRESS = "NO_ACCESS_COMMERCIAL_ADDRESS",
  NO_ACCESS_RESIDENTIAL_ADDRESS = "NO_ACCESS_RESIDENTIAL_ADDRESS",
  UNABLE_TO_COLLECT_SIGNATURE = "UNABLE_TO_COLLECT_SIGNATURE",
  CUSTOMER_REFUSED_DELIVERY = "CUSTOMER_REFUSED_DELIVERY",
  INCORRECT_CUSTOMER_ADDRESS = "INCORRECT_CUSTOMER_ADDRESS",
  MISSING_PACKAGE = "MISSING_PACKAGE",
  DRIVER_ACCIDENT = "DRIVER_ACCIDENT",
  ITEM_DAMAGED = "ITEM_DAMAGED",
  PAST_DELIVERY_WINDOW = "PAST_DELIVERY_WINDOW",
  NO_SPACE_IN_CAR = "NO_SPACE_IN_CAR",
  OTHER = "OTHER",
}

export enum WebhookTopic {
  TRACK_LABEL_UPDATES = "TRACK_LABEL_UPDATES",
}

export enum WebhookStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}
/**
 * Webhook configurations so that we can keep track of the merchant notification url, and failure count.
 * If failure count is equal to 300 then we unsubscribe the merchant from the webhook by turning the status to INACTIVE.
 * Otherwise if the status is ACTIVE then we send the information to the merchant notificationUrl. In the future we may
 * have more than one webhook and the merchant will be able to configure and subscribe to different TOPICS.
 */
export type IMerchantWebhooks = {
  [key in WebhookTopic]?: IMerchantWebhook;
};

export interface IMerchantWebhook {
  notificationUrl: string;
  status: WebhookStatus;
  failureCount: number;
  failureMsg?: string;
}

export interface IMerchantTrackingEventRequest {
  id: ILabelV2["id"];
  /** Snake casing since the response is done through a firebase trigger and does not have middleware to transform it from camelCasing.
   *  This allows us to stay consistent with the api. */
  tracking_number: ILabelV2["trackingNumber"];
  state: ITrackingEvent["state"];
  timestamp?: ITrackingEvent["timestamp"];
}

export type IMerchantWebhookSubscriptionRequest = Pick<
  IMerchantWebhook,
  "notificationUrl" | "status"
>;

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

/** Request sent to external order fulfillment endpoint */
export interface IFulfillExternalOrdersRequest {
  labelIds: string[];
}

/** Response sent from external order fulfillment endpoint */
export interface IFulfillExternalOrdersResponse {
  shopifySuccessOrderNumbers: string[]; // Array of successful fulfillment shopify order numbers
  shopifyFailureOrderNumbers: string[]; // Array of failed fulfillment shopify order numbers
  shipStationSuccessOrderNumbers: string[]; // Array of successful fulfillment shopify order numbers
  shipStationFailureOrderNumbers: string[]; // Array of failed fulfillment shopify order numbers
  manualLabelIds: string[]; //Array of manual created label ids
}

/**  Addresses to be excluded from swyft shipping */
export interface IExcludedShippingAddress {
  id: string;
  /* Merchant this shipping address belongs to */
  merchantId: string;
  /* Raw address before validation */
  raw: IAddress;
  /* Validated Address */
  validated: IValidatedAddressV2;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface ShopifyGdprCustomer {
  id: number;
  email?: string;
  phone?: string;
}

interface ShopifyGdprBase {
  shop_id: string;
  shop_domain: string;
  customer: ShopifyGdprCustomer;
}

interface ShopifyDataRequest extends ShopifyGdprBase {
  orders_requested: number[];
  data_request: {
    id: number;
  };
}

interface ShopifyDataRedact extends ShopifyGdprBase {
  orders_to_redact: number[];
}

interface ShopifyShopRedact {
  shop_id: string;
  shop_domain: string;
}

export type ShopifyGdprPrivacyRequest =
  | ShopifyShopRedact
  | ShopifyDataRedact
  | ShopifyDataRequest;

export type ShopifyGdprPrivacyServiceRequest = {
  raw: ShopifyGdprPrivacyRequest;
};

export type IShopifyGdprPrivacy = {
  id: string;
  createdAt: string;
  raw: ShopifyGdprPrivacyRequest;
};

export type IOnfleetWebhookInstance = {
  id: string;
  webhookType: string;
  taskId: string;
  timeLastModified: string;
};

/** Interface of customer shipping experience model */
export interface ICustomerShippingExperience {
  id: string;
  trackingNumber: string;
  rating: string;
  context?: string;
  createdAt: string | FieldValue;
}

export interface IntegrationProperties {
  id: string;
  name: string;
  type: string;
  value: string;
}

export enum IntegrationType {
  STORES = "STORES",
  TMS = "TMS",
  CARRIERS = "CARRIERS",
}

export interface Integration {
  id: string;
  type: IntegrationType;
  active: boolean;
  name: string;
  logo: string;
  description: string;
  properties: IntegrationProperties[];
}

export interface MerchantIntegration {
  integration: Integration;
  properties: IntegrationProperties[];
}
